import React, {useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {
    Alert,
    Autocomplete,
    Box,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import ScreenWrapper from "crqlar-react-common/dist/components/ScreenWrapper";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {
    usePrintTemplates
} from "crqlar-react-common/dist/utils/hooks/templates/usePrintTemplates";
import {
    PrintTemplate,
    PrintTemplateType
} from "crqlar-react-common/dist/models/template/printTemplate.model";
import AgendaPreviewContent from "./AgendaPreviewContent";
import {useTranslation} from "react-i18next";
import {
    useCalculatedAgenda
} from "crqlar-react-common/dist/utils/hooks/agenda/useAgendaData";
import Button from "@mui/material/Button";
import CDatePicker from "crqlar-react-common/dist/components/CDatePicker";
import PrintIcon from "@mui/icons-material/Print";
import {endOfDay, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const languageOptions = [
    {value: "de", label: "German"},
    {value: "en", label: "English"},
    {value: "de+en", label: "German / English"},
    {value: "en+de", label: "English / German"},
];


const PublicAgendaPrintout = () => {
    const {t} = useLocalize();
    const {i18n} = useTranslation();

    const {agendaId} = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const query = new URLSearchParams(location.search);
    const dateParam = query.get("date");


    const [language, setLanguage] = useState<string>(() => {
        const lang = (i18n.language || "").toLowerCase();
        return lang.startsWith("de") ? "de" : lang.startsWith("en") ? "en" : "de";
    });

    const primaryLanguage = useMemo(() => language.split("+")[0], [language]);
    const secondaryLanguage = useMemo(() => {
        const parts = language.split("+");
        return parts.length > 1 ? parts[1] : null;
    }, [language]);


    // templates
    const {data: templatesData, isLoading: isPending} = usePrintTemplates();
    const agendaTemplates = useMemo(() => {
        return templatesData?.content?.filter(template => template.type === PrintTemplateType.AGENDA_HANDOUT) || []
    }, [templatesData]);
    const [activeTemplate, setActiveTemplate] = useState<PrintTemplate | null>(agendaTemplates.length > 0 ? agendaTemplates[0] : null)

    useEffect(() => {
        if (agendaTemplates.length > 0 && !activeTemplate) {
            setActiveTemplate(agendaTemplates[0])
        }

    }, [agendaTemplates, activeTemplate])

    // agenda
    const initialDate = dateParam ? new Date(dateParam) : new Date();

    const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
        start: initialDate,
        end: initialDate
    });


    const {
        data,
        isLoading
    } = useCalculatedAgenda(agendaId, startOfDay(dateRange.start), endOfDay(dateRange.end));


    // agenda styles
    useEffect(() => {
        if (data?.agenda?.uiConfig?.font?.font?.mediaURI) {
            const linkElement = document.createElement('link');
            linkElement.rel = 'stylesheet';
            linkElement.href = data.agenda.uiConfig.font.font.mediaURI;
            document.head.appendChild(linkElement);

            return () => {
                document.head.removeChild(linkElement);
            };
        }
    }, [data]);


    return (
        <ScreenWrapper>
            <style>{`
                @media print {
                    .hide-on-print {
                        display: none !important;
                    }
                    .print-container {
                        box-shadow: none !important;
                        width: 100% !important;
                        min-height: auto !important;
                        padding: 10mm !important;
                        margin: 0 !important;
                    }
                    @page {
                        margin: 0;
                        size: A4 portrait;
                    }
                    body {
                        margin: 0;
                        padding: 0;
                    }
                    table, tr, td, th {
                        page-break-inside: avoid;
                    }
                    * {
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                        color-adjust: exact;
                        box-shadow: none !important;
                    }
                }
            `}</style>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                    width: '100%'
                }}
            >
                {isPending ?
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '400px'
                        }}>
                        <CircularProgress size={60}/>
                    </Box>
                    :
                    <Card className="hide-on-print"
                          sx={{width: '100%', maxWidth: '800px'}}>

                        <CardContent sx={{position: "relative"}}>


                            <Typography gutterBottom
                                        sx={{
                                            color: 'text.secondary',
                                            fontSize: 14
                                        }}>
                                {t("Print Configuration")}
                            </Typography>

                            <Stack spacing={2} sx={{mt: 2}} direction="row">
                                <Box sx={{width: '200px'}}>
                                    {agendaTemplates.length === 0 ? (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => navigate('/system/printTemplates')}
                                        >
                                            {t('Create')}
                                        </Button>
                                    ) : (
                                        <Autocomplete
                                            id="template-selector"
                                            options={agendaTemplates}
                                            getOptionLabel={(option: PrintTemplate) => option.name || ''}
                                            value={activeTemplate}
                                            renderInput={(params) =>
                                                <TextField {...params}
                                                           label={t('Template')}/>}
                                            onChange={(_, value) => setActiveTemplate(value)}
                                            sx={{flex: 1}}
                                            size="small"
                                        />
                                    )}
                                </Box>

                                <Box sx={{width: '200px'}}>
                                    <Autocomplete
                                        id="language-selector"
                                        options={languageOptions}
                                        value={languageOptions.find(o => o.value === language) || null}
                                        getOptionLabel={(option) => t(option.label)}
                                        onChange={(_, value) => {
                                            if (value) {
                                                setLanguage(value.value);
                                            }
                                        }}
                                        renderInput={(params) =>
                                            <TextField {...params}
                                                       label={t('Language')}/>}
                                        size="small"
                                    />
                                </Box>

                                <Box>
                                    <CDatePicker
                                        dateOnly
                                        select="range"
                                        value={[new Date(dateRange.start), new Date(dateRange.end)]}
                                        onChangeRange={(from, to) => {
                                            setDateRange({
                                                start: from || new Date(),
                                                end: to || new Date()
                                            });
                                        }}
                                        onChange={(date) => {
                                            const isoDate = date || new Date();
                                            setDateRange({
                                                start: isoDate,
                                                end: isoDate
                                            });
                                        }}
                                        size="small"
                                        label={t('Date range')}
                                        sx={{width: 250}}
                                    />
                                </Box>
                            </Stack>

                            {agendaTemplates.length === 0 && <Box sx={{mt: 2}}>
                                <Alert severity="info" action={
                                    <Box>
                                        <Button color="inherit" size="small"
                                                component="a"
                                                href={`/system/printTemplates`}
                                                target="_blank"
                                                sx={{whiteSpace: "pre"}}
                                                rel="noopener noreferrer"
                                                endIcon={<OpenInNewIcon/>}>
                                            {t('Manage Templates')}
                                        </Button>
                                    </Box>
                                }>
                                    {t("Consider creating a print template in order to customize banner image as well as header and footer text blocks.")}
                                </Alert>

                            </Box>}
                        </CardContent>

                        <CardActions
                            sx={{p: 2, pt: 0,}}>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => window.print()}
                                startIcon={<PrintIcon/>}
                            >
                                {t('Print')}
                            </Button>
                            {/* The old admin-only "Try new printout" teaser linked
                              * /print/printout/agenda/<agendaId> back when that route only showed an
                              * info alert. That route is now the GUEST agenda handout and its :id is
                              * a profile id — this screen's agendaId (a public-agenda entity) is the
                              * wrong entity for it, so the teaser is gone rather than broken. */}
                            <Button color="inherit"
                                    component="a"
                                    href={`/system/printTemplates`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    endIcon={<OpenInNewIcon/>}>
                                {t('Manage Templates')}
                            </Button>

                        </CardActions>
                    </Card>
                }

                {data ? <div
                        className="print-container"
                        style={{
                            width: '210mm',
                            minHeight: '297mm',
                            padding: '10mm',
                            boxSizing: 'border-box',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            justifyContent: 'flex-start',
                            fontFamily: data.agenda?.uiConfig?.font?.family || undefined,
                            fontSize: '7pt',
                            color: '#000000',
                            backgroundColor: '#FFFFFF',
                            boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                        }}
                    >

                        <AgendaPreviewContent
                            activeTemplate={activeTemplate}
                            primaryLanguage={primaryLanguage}
                            secondaryLanguage={secondaryLanguage}
                            dateRange={dateRange}
                            items={data?.items ?? []}
                        />


                    </div>
                    :
                    <>


                        {isLoading ? <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '400px'
                        }}>
                            <CircularProgress size={60}/>
                        </Box> : <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '400px'
                        }}>
                            <div>{t('Agenda not found')}</div>
                        </Box>}

                    </>

                }

            </Box>
        </ScreenWrapper>
    );
};

export default PublicAgendaPrintout;